import { Button } from '@/components/ui/button';
import { FlexCol } from '@/components/ui/flex';
import { Textarea } from '@/components/ui/textarea';
import {
  Tooltip,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { cn } from '@/utils/classUtils';
import { ArrowUpIcon } from 'lucide-react';
import {
  useEffect,
  useRef,
  type ChangeEvent,
  type FC,
  type FormEvent,
  type KeyboardEvent,
} from 'react';

interface InputProps {
  onSubmit: (text: string) => void;
  currentPrompt: string;
  setCurrentPrompt: (prompt: string) => void;
  rows?: number;
}

export const Input: FC<InputProps> = ({
  onSubmit,
  currentPrompt,
  setCurrentPrompt,
  rows = 2,
}) => {
  const textAreaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    adjustTextareaHeight();
  }, [currentPrompt]);

  const adjustTextareaHeight = () => {
    if (textAreaRef.current) {
      textAreaRef.current.style.height = 'auto';
      const newHeight = Math.min(textAreaRef.current.scrollHeight, 200);
      textAreaRef.current.style.height = `${newHeight}px`;
    }
  };

  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!currentPrompt.trim()) return;

    onSubmit(currentPrompt);
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (isDisabled) return;
    if (!currentPrompt.trim()) return;

    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      onSubmit(currentPrompt);
    }
  };

  const handleInputChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
    setCurrentPrompt(e.target.value);
  };

  const isDisabled = !currentPrompt.trim();

  return (
    <FlexCol gap="2" className="w-full">
      <form onSubmit={handleSubmit} className="w-full">
        <div
          className={cn(
            'w-full flex items-end p-4 space-x-2',
            'border border-neutral-300 rounded-t-lg border-b-0 mb-[1px] bg-white'
          )}
        >
          <div className="w-full">
            <Textarea
              className={cn(
                'w-auto flex-grow border-none resize-none shadow-none focus:ring-0 p-0 focus-visible:ring-0',
                'text-base text-primary placeholder:text-base placeholder:text-tertiary'
              )}
              placeholder="Ask anything about medical device regulations..."
              ref={textAreaRef}
              rows={rows}
              onKeyDown={handleKeyDown}
              onInput={handleInputChange}
              value={currentPrompt}
              style={{
                maxHeight: '200px',
                overflowY: 'auto',
              }}
            />
          </div>

          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant={isDisabled ? 'secondary' : 'default'}
                  className="rounded-full min-h-[36px] h-[36px] min-w-[36px] w-[36px]"
                  disabled={isDisabled}
                  size="sm"
                  type="submit"
                >
                  <ArrowUpIcon className="w-4 h-4" />
                </Button>
              </TooltipTrigger>
            </Tooltip>
          </TooltipProvider>
        </div>
      </form>
    </FlexCol>
  );
};
